import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type RecentBehaviourResult } from '@whinerp/client';
import type { FlowGraph, FlowNode } from '@whinerp/contracts/flow';
import { formatDateTime } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * A journey's entry (F-069): what starts it, what exactly it waits for, and
 * the recent-events inspector - what arrived and what it started, or why not.
 */

const TRIGGERS = ['entered-segment', 'page-viewed', 'form-submitted', 'email-clicked', 'custom-event', 'field-changed'] as const;
const FIELDS: Record<string, Array<{ key: string; label: 'journey.urlContains' | 'journey.embedId' | 'journey.campaignId' | 'journey.eventName' | 'journey.field' | 'journey.value' }>> = {
  'page-viewed': [{ key: 'urlContains', label: 'journey.urlContains' }],
  'form-submitted': [{ key: 'embedId', label: 'journey.embedId' }],
  'email-clicked': [{ key: 'campaignId', label: 'journey.campaignId' }],
  'custom-event': [{ key: 'eventName', label: 'journey.eventName' }],
  'field-changed': [
    { key: 'field', label: 'journey.field' },
    { key: 'value', label: 'journey.value' },
  ],
};
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function JourneyTriggerPanel({ graph, onChange }: { graph: FlowGraph; onChange: (next: FlowGraph) => void }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const enabled = useEntitlement('F-069');
  const events = useQuery<RecentBehaviourResult, ApiError>({ queryKey: ['recent-behaviour'], queryFn: () => api.recentBehaviour({}), enabled: enabled.enabled, refetchInterval: 15_000 });
  const entry = graph.nodes.find((node) => node.kind === 'trigger');
  if (!enabled.enabled || !entry) return null;
  const setEntry = (next: Partial<FlowNode>) =>
    onChange({ ...graph, nodes: graph.nodes.map((node) => (node.id === entry.id ? { ...node, ...next } : node)) } as FlowGraph);
  const config = (entry.config ?? {}) as Record<string, string>;
  return (
    <Panel data-region="journey-trigger">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('journey.entry')}</SectionLabel>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('journey.startsWhen')}
            <select data-journey-trigger className={INPUT} value={entry.type} onChange={(e) => setEntry({ type: e.target.value, label: t(`journey.trigger.${e.target.value as (typeof TRIGGERS)[number]}`), config: {} })}>
              {TRIGGERS.map((type) => (
                <option key={type} value={type}>{t(`journey.trigger.${type}`)}</option>
              ))}
            </select>
          </label>
          {(FIELDS[entry.type] ?? []).map((field) => (
            <label key={field.key} className="flex flex-col gap-1 text-dense text-ink-600">
              {t(field.label)}
              <input data-journey-trigger-field={field.key} className={INPUT} value={config[field.key] ?? ''} onChange={(e) => setEntry({ config: { ...config, [field.key]: e.target.value } })} />
            </label>
          ))}
        </div>
        <SectionLabel>{t('journey.recentEvents')}</SectionLabel>
        <ul data-recent-events className="flex flex-col gap-1">
          {(events.data?.events ?? []).slice(0, 15).map((event) => (
            <li key={event.eventId} data-recent-event={event.kind} className="text-meta text-ink-600">
              {formatDateTime(event.occurredAt, format)} · {t(`journey.trigger.${event.kind}`)} · {event.person ?? t('journey.anonymous')} · {event.outcome}
            </li>
          ))}
          {events.data && events.data.events.length === 0 ? <li className="text-meta text-ink-meta">{t('journey.noEvents')}</li> : null}
        </ul>
      </div>
    </Panel>
  );
}
